import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdStatus, AspectRatioResolutionsTooltip, CreativesModal, MacrosList } from 'sonata-platform-components';
import {
  ButtonUI,
  Checkbox,
  ContextualMenu,
  EmptyState,
  FieldEdition,
  fetchGeolocationUserGoogle,
  InputSearch,
  Loader,
  Switch,
  TaptapList,
  Toast,
  Tooltip,
  TooltipInteractionEnum,
  useQueryFetch
} from 'sonata-react-components';
import { editInsertionOrder } from 'sonata-static-storage/lib/entities';
import {
  formatMataHariScreenToPlatformScreen,
  getDoohAdTypeFromAdFormat
} from 'sonata-static-storage/lib/helpers/ads.helpers';
import { getDoohAdFormatsCatalog } from 'sonata-static-storage/lib/helpers/creative.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { PRICING_MODELS } from 'sonata-static-storage/lib/samples/pricingModels.samples';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { DevicePlatformEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/devicePlatform.schema';
import { Creative } from 'sonata-static-storage/lib/schemas/creative/creative.schema';

import { selectCreativeGroups } from '@/components/common/MultiFlightMap/grouping/domain/groupQueries';
import { adToValidate, getScreenSourcesPublishersDictionary } from '@/services/common.service';
import { getCreativesByUserId } from '@/services/creatives.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import {
  buildPreviewUrl,
  equalizeAdsDistribution,
  getAdCreativesGridLayout,
  type PreviewPosition
} from './AdCreatives.helper';
import AdCreativesColumns from './AdCreativesColumns';
import { AddCreatives } from './AddCreatives/AddCreatives';
import { AdPrice } from './AdPrice/AdPrice';
import { AdTypeScreens } from './AdTypeScreens/AdTypeScreens';
import { Behavior } from './Behavior/Behavior';
import { BulkActions } from './BulkActions/BulkActions';
import { Distribution } from './Distribution/Distribution';
import { Duration } from './Duration/Duration';
import { Group } from './Group/Group';
import { QREnabled } from './QREnabled/QREnabled';

import type {
  DoohAdFormat,
  DoohScreenAspectRatio,
  MultiFlightEntityType,
  WizardFormattedScreen
} from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrder, LineItem, LineItemGoal } from 'sonata-static-storage/lib/entities';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './adCreatives.scss';

function getBudgetsByDistribution(ads: AdInterface<string>[], totalBudget: number) {
  return ads.map(ad => ({
    _id: ad._id,
    budget: ((ad.distribution || 0) / 100) * totalBudget
  }));
}

function getDistributionsByBudget(ads: AdInterface<string>[], adId: string, newBudget: number, totalBudget: number) {
  const validatedNewBudget = Math.min(newBudget, totalBudget);
  const activeOtherAds = ads.filter(ad => ad._id !== adId).filter(ad => ad.visible && !ad.deleted);
  const remainingBudget = Math.max(totalBudget - validatedNewBudget, 0);
  const sumOtherBudgets = activeOtherAds.reduce((acc, ad) => acc + ((ad.distribution || 0) / 100) * totalBudget, 0);
  const sumOtherAdDistributions = activeOtherAds.reduce((acc, ad) => acc + (ad.distribution || 0), 0);
  const otherAdsCount = activeOtherAds.length;

  return ads.map(ad => {
    if (ad._id === adId) {
      const newDistribution = Math.round((validatedNewBudget / totalBudget) * 100);
      return {
        ...ad,
        distribution: newDistribution
      };
    } else {
      if (!ad.visible || ad.deleted) {
        return ad;
      }

      let newDistributionValue = 0;

      if (sumOtherAdDistributions > 0) {
        const prevBudget = ((ad.distribution || 0) / 100) * totalBudget;
        const newBudgetForAd = sumOtherBudgets > 0 ? (prevBudget / sumOtherBudgets) * remainingBudget : 0;
        newDistributionValue = Math.round((newBudgetForAd / totalBudget) * 100);
      } else if (remainingBudget > 0 && otherAdsCount > 0) {
        const remainingDistribution = Math.round((remainingBudget / totalBudget) * 100);
        newDistributionValue = Math.floor(remainingDistribution / otherAdsCount);
      }

      return {
        ...ad,
        distribution: newDistributionValue
      };
    }
  });
}

type TaptapListItem = {
  id: string;
  budgetValue: string;
  creative: Creative;
  aspectRatio?: DoohScreenAspectRatio;
  disabled: boolean;
  error: boolean;
  format: string;
  isDirty: boolean;
  status: 'default' | 'notVisible';
  isRedirect: boolean;
  title: AdInterface['campaignName'];
  subtitle: string;
  onChange: (groups: string[]) => void;
  onChangeBudgetDistribution?: (value: string | FileList | null) => void;
  onChangeInputDistribution?: (value: string | FileList | null) => void;
  onChangeRangeDistribution?: (value: number) => void;
  screens: WizardFormattedScreen[];
  ad: AdInterface<string>;
};

type State = {
  editedAds: AdInterface<string>[];
  search: string | null;
  selectedAdIds: string[];
  showAdPriceColumn: boolean;
  showDistributionColumn: boolean;
  showErrorToast: boolean;
  withAutoDistribution: boolean;
};

const MAX_DISTRIBUTION = 100;

export function AdCreatives({
  entity,
  errors,
  goals,
  entityType,
  onAdsChange,
  onVenuesChange,
  onAdsDistributionToggled,
  onAdsPriceToggled,
  onCustomEventsToggled,
  savedBaseline,
  screens,
  user,
  userRoles,
  venues
}: Readonly<{
  entity: InsertionOrder | LineItem;
  errors?: Record<string, any>;
  goals?: LineItemGoal[];
  entityType: Omit<MultiFlightEntityType, 'campaignPlan'>;
  onAdsDistributionToggled: (newState: boolean) => void;
  onAdsChange: (ads: AdInterface<string>[]) => void;
  onAdsPriceToggled: (newState: boolean) => void;
  onCustomEventsToggled: (newState: boolean) => void;
  onVenuesChange: (venues: Venue<string>[]) => void;
  savedBaseline: Record<string, unknown>;
  screens: GeoDataAndScreensInteface[];
  user: UserInterface;
  userRoles: UserRoleInterface[];
  venues: Venue<string>[];
}>) {
  const { formatMessage } = useIntl();
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const showDistribution = checkUserResourcePermissions('adCreativePacing', user, userRoles);
  const showAdPrice = checkUserResourcePermissions('adCreativePrice', user, userRoles);
  const showCustomEvents = checkUserResourcePermissions('customEvents', user, userRoles);
  const showAdLink = checkUserResourcePermissions('showAdLink', user, userRoles);

  const isDistributionColumnActive =
    showDistribution &&
    entity.adsDistribution &&
    entity.ads.some(ad => !ad.deleted && ad.visible && (ad.distribution || 0) > 0);

  const isAdPriceColumnActive =
    showAdPrice && entity.adsPriceEnabled && entity.ads.some(ad => !ad.deleted && ad.visible && (ad.bidPrice || 0) > 0);

  const isCustomEventsActive = Boolean(entity.customEvents);

  const [
    {
      editedAds,
      search,
      selectedAdIds,
      showAdPriceColumn,
      showDistributionColumn,
      showErrorToast,
      withAutoDistribution
    },
    setState
  ] = useState<State>({
    editedAds: [],
    search: null,
    selectedAdIds: [],
    showAdPriceColumn: !!isAdPriceColumnActive,
    showDistributionColumn: !!isDistributionColumnActive,
    showErrorToast: true,
    withAutoDistribution: true
  });

  const [previewPosition, setPreviewPosition] = useState<PreviewPosition | undefined>();

  // Resolved up front so the preview button can open the tab synchronously (an async
  // window.open would be blocked as a popup). Falls back to 0,0 if geolocation fails.
  useEffect(() => {
    fetchGeolocationUserGoogle({}, taptap.config.googleApiKey)
      .then(({ location }) => setPreviewPosition(location))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (errors?.ads) {
      setState(prevState => ({
        ...prevState,
        showErrorToast: true
      }));
    }
  }, [errors?.ads]);

  const toggleAutoDistribution = () => {
    setState(prev => {
      const toggled = !prev.withAutoDistribution;
      let updatedAds = entity.ads;

      if (toggled) {
        const activeAdsCount = entity.ads.filter(ad => !ad.deleted && ad.visible).length;
        const equalDistribution = activeAdsCount > 0 ? Math.floor(MAX_DISTRIBUTION / activeAdsCount) : 0;
        let remainder = activeAdsCount > 0 ? MAX_DISTRIBUTION % activeAdsCount : 0;

        updatedAds = entity.ads.map(ad => {
          if (!ad.deleted && ad.visible) {
            let distribution = equalDistribution;
            if (remainder > 0) {
              distribution += 1;
              remainder--;
            }
            return { ...ad, distribution: distribution };
          }
          return ad;
        });

        onAdsChange(updatedAds);
      }

      return {
        ...prev,
        withAutoDistribution: toggled
      };
    });
  };

  const { data: publishersDictionary, isFetching: isFetchingPublishersDictionary } = useQueryFetch(
    ['getScreenSourcesPublishersDictionary'],
    async () => getScreenSourcesPublishersDictionary({})
  );

  const creativesToken = {};
  const creativesIds = entity.ads.map(({ creative }) => creative);
  const { data: creatives, isFetching: isFetchingCreatives } = useQueryFetch(
    ['getCreatives', user.id, creativesIds.join(',')],
    async () => {
      const response = await getCreativesByUserId(user.id!, creativesIds, creativesToken);
      return response.items;
    },
    {
      enabled: Boolean(user.id) && !!entity.ads.length
    }
  );

  // Handlers can fire back-to-back before `entity.ads` (a prop) picks up the
  // previous update, so we track the latest ads in a ref instead of trusting
  // the prop to be current within the same tick.
  const adsRef = useRef(entity.ads);
  adsRef.current = entity.ads;

  const handleAdsChange = (updateCreativeFn: (currentAds: AdInterface<string>[]) => AdInterface<string>[]) => {
    const updatedAds = updateCreativeFn(adsRef.current);
    adsRef.current = updatedAds;
    onAdsChange(updatedAds);
  };

  // Auto-rebalancing only makes sense while the entity's own distribution
  // feature is on; `withAutoDistribution` alone can't gate it, or it fires
  // even when `entity.adsDistribution` is disabled.
  const equalizeIfEnabled = (ads: AdInterface<string>[]) =>
    entity.adsDistribution && withAutoDistribution ? equalizeAdsDistribution(ads) : ads;

  const handleVisibilityChange = (adId: string, isVisible: boolean) => {
    handleAdsChange(currentAds => {
      const updatedAds = currentAds.map(ad => {
        if (ad._id === adId) {
          return {
            ...ad,
            distribution: isVisible ? ad.distribution : 0,
            visible: isVisible
          };
        }
        return ad;
      });
      return equalizeIfEnabled(updatedAds);
    });
  };

  const handleNameChange = (adId: string, name: string) => {
    handleAdsChange(currentAds => {
      return currentAds.map(ad => {
        if (ad._id === adId) {
          return {
            ...ad,
            campaignName: name
          };
        }
        return ad;
      });
    });
  };

  const handleGroupsChange = (adId: string, groups: string[]) => {
    handleAdsChange(currentAds => {
      return currentAds.map(ad => {
        if (ad._id === adId) {
          return {
            ...ad,
            ...(entity.type === 'dooh'
              ? {
                  screenGroups: groups
                }
              : {
                  zoneGroups: groups
                })
          };
        }
        return ad;
      });
    });
  };

  const handleAddAdCreatives = (newAds: AdInterface<string>[]) => {
    handleAdsChange(currentAds => equalizeIfEnabled([...currentAds, ...newAds]));
  };

  const handleEditCreative = (ad: AdInterface<string>) =>
    setState(prevState => ({
      ...prevState,
      editedAds: [cloneDeep(ad)]
    }));

  const handleCloseEditCreativeModal = () =>
    setState(prevState => ({
      ...prevState,
      editedAds: []
    }));

  const handleConfirmEditCreative = (modifiedAds: AdInterface<string>[]) => {
    const modifiedAd = modifiedAds[0];
    if (!modifiedAd) {
      return;
    }
    handleAdsChange(currentAds => currentAds.map(ad => modifiedAds.find(mAd => mAd._id === ad._id) || ad));
    setState(prevState => ({
      ...prevState,
      editedAds: []
    }));
  };

  const handleDeleteCreative = (adId: string) => {
    handleAdsChange(currentAds => equalizeIfEnabled(currentAds.filter(ad => ad._id !== adId)));
  };

  const handleSearchChange = (value: string) => {
    setState(prevState => ({
      ...prevState,
      search: value
    }));
  };

  const updateAdsAdExchangesValidation = async (insertionOrder: InsertionOrder, adsAdExchangesValidation: any) => {
    const nonDeletedAds = insertionOrder.ads.filter(ad => !ad.deleted);
    const updatedAd = nonDeletedAds.find(ad => ad._id === adsAdExchangesValidation[0].adId);
    if (updatedAd) {
      updatedAd.adExchanges = adsAdExchangesValidation[0].adExchanges;
    }
    const newInsertionOrder = {
      ...insertionOrder,
      ads: insertionOrder.ads.map(ad => (ad._id === updatedAd?._id ? updatedAd : ad))
    };
    await editInsertionOrder(user.id!, insertionOrder._id!, newInsertionOrder);
  };

  const handleUpdateAdsValidationCallback = (
    exchangeCode: string,
    ad: AdInterface<string>,
    entityObj: InsertionOrder | LineItem
  ) => {
    adToValidate(exchangeCode, ad, entityObj, updateAdsAdExchangesValidation, formatMessage);
  };
  const filteredAds = entity.ads.filter(ad => ad.campaignName.toLowerCase().includes(search || ''.toLowerCase()));

  const handleSelectedAdIdsChange = (selectedItems: Record<string, any>[]) => {
    setState(prevState => ({
      ...prevState,
      selectedAdIds: selectedItems.map(item => item.id)
    }));
  };

  const handleDistributionChange = (adId: string, newDistribution: number) => {
    const validatedDistribution = Math.max(0, Math.min(MAX_DISTRIBUTION, newDistribution));
    handleAdsChange(currentAds => {
      let updatedAds = currentAds;
      if (withAutoDistribution) {
        updatedAds = equalizeAdsDistribution(currentAds, {
          _id: adId,
          distribution: validatedDistribution
        });
      } else {
        updatedAds = currentAds.map(ad => (ad._id === adId ? { ...ad, distribution: validatedDistribution } : ad));
      }
      updatedAds = updatedAds.map(ad => {
        if (!ad.deleted && ad.visible) {
          const distribution = ad._id === adId && !withAutoDistribution ? validatedDistribution : ad.distribution;
          return {
            ...ad,
            distribution: distribution
          };
        }
        return ad;
      });
      return updatedAds;
    });
  };

  const handleChangeDistribution = (adId: string) => (value: number | string | FileList | null) => {
    let distribution: number = 0;
    if (typeof value === 'number') {
      distribution = value;
    } else if (typeof value === 'string') {
      distribution = parseInt(value) || 0;
    }
    handleDistributionChange(adId, distribution);
  };

  const handleBudgetDistributionChange = (adId: string) => (value: string | FileList | null) => {
    if (!withAutoDistribution) {
      return;
    }
    const totalBudget = goals ? goals[0]?.target || 0 : 0;
    const newBudget = parseFloat(value as string) || 0;
    handleAdsChange(currentAds => {
      const newAds = getDistributionsByBudget(currentAds, adId, newBudget, totalBudget);
      return newAds;
    });
  };

  const entityPricingModel = entity.pricingModelConfig?.bidModels[0].bid.pricingModel;
  const handleAdPriceChange = (adId: string) => (value: string | FileList | null) => {
    const inputValue = parseFloat(value as string) || 0;
    const pricingModel = PRICING_MODELS.find(({ model }) => model === entityPricingModel);

    handleAdsChange(currentAds => {
      return currentAds.map(ad => {
        if (ad._id === adId) {
          let bidPrice = 0;
          if (pricingModel?.perMil) {
            bidPrice = inputValue / 1000;
          } else {
            bidPrice = inputValue;
          }
          return { ...ad, bidPrice };
        }
        return ad;
      });
    });
  };

  const totalBudget = goals ? goals[0]?.target || 0 : 0;
  const budgetsByAd = getBudgetsByDistribution(entity.ads, totalBudget);
  const savedBaselineAds = (savedBaseline as { ads?: AdInterface<string>[] }).ads ?? [];
  const isAdUnsaved = (ad: AdInterface<string>) => {
    const baselineAd = ad._id ? savedBaselineAds.find(b => b._id === ad._id) : undefined;
    return !baselineAd || !isEqual(ad, baselineAd);
  };
  const creativeGroups =
    entity.type === 'dooh'
      ? selectCreativeGroups('screens', entity.screenGroups ?? [], screens)
      : selectCreativeGroups('locations', entity.zoneGroups ?? [], []);
  const formattedScreens = screens.map(screen => formatMataHariScreenToPlatformScreen(screen as any));
  const filteredAdsByAdType = [...new Set(formattedScreens.map(s => s.supportedAds).flat())];
  const aspectRatiosByAdType = filteredAdsByAdType.map(adType => {
    return {
      adType,
      aspectRatios: getDoohAdFormatsCatalog(formattedScreens, adType)
    };
  });

  const gridLayout = getAdCreativesGridLayout({
    entityType,
    family: entity.type,
    showAdLink,
    showAdPrice,
    showAdPriceColumn,
    showDistribution,
    showDistributionColumn
  });

  const compositionData: TaptapListItem[] =
    isFetchingPublishersDictionary || isFetchingCreatives
      ? []
      : filteredAds.map(ad => {
          const creative = creatives?.find(c => c._id === ad.creative)!;
          const budgetObj = budgetsByAd.find(b => b._id === ad._id)!;
          const isDisabled = !ad.visible;
          const budgetValue = isDisabled ? '0.000' : budgetObj ? budgetObj.budget.toFixed(3) : '0.000';
          const distributionValue = isDisabled ? 0 : ad.distribution;

          const adAdType = getDoohAdTypeFromAdFormat(ad.adFormat as DoohAdFormat);
          const aspectRatios = aspectRatiosByAdType.find(({ adType }) => adAdType === adType)?.aspectRatios;
          const aspectRatio = aspectRatios?.find(({ id }) => creative?.aspectRatio === id);
          const entityPricingModel = entity.pricingModelConfig?.bidModels[0].bid.pricingModel;
          const pricingModel = PRICING_MODELS.find(({ model }) => model === entityPricingModel);
          let visualBidPrice = typeof ad.bidPrice === 'number' ? ad.bidPrice : 0;
          if (showAdPrice && showAdPriceColumn) {
            if (pricingModel?.perMil && typeof visualBidPrice === 'number') {
              visualBidPrice *= 1000;
            }
          }

          const filteredScreens =
            Array.isArray(ad.screenGroups) && ad.screenGroups.length > 0
              ? formattedScreens.filter(
                  screen => Array.isArray(screen.groups) && screen.groups.some(group => ad.screenGroups.includes(group))
                )
              : formattedScreens;

          return {
            ad: {
              ...ad,
              id: ad._id,
              onChange: handleAdPriceChange(ad._id!),
              ...(entityType === 'lineItem' &&
                showDistribution &&
                showDistributionColumn && { distribution: distributionValue }),
              ...(showAdPrice &&
                showAdPriceColumn && {
                  bidPrice: typeof visualBidPrice === 'number' ? visualBidPrice : 0
                })
            },
            aspectRatio,
            baseCurrency: currencyInfo,
            budgetValue,
            creative,
            defaultGroupValue: entity.type === 'dooh' ? ad.screenGroups : ad.zoneGroups,
            disabled: isDisabled,
            entityFamily: entity.type,
            error: Boolean(errors?.ads?.message),
            format: formatMessage({
              defaultMessage: ad.adFormat,
              id: `multiFlightCommon.bannerFormats.${ad.adWidth}x${ad.adHeight}${ad.adFormat}`
            }),
            groups: creativeGroups,
            id: ad._id!,
            isDirty: isAdUnsaved(ad),
            isRedirect: Boolean(creative?.redirect),
            onChange: (groups: string[]) => handleGroupsChange(ad._id!, groups),
            onChangeBudgetDistribution: isDisabled ? undefined : handleBudgetDistributionChange(ad._id!),
            onChangeInputDistribution: isDisabled ? undefined : handleChangeDistribution(ad._id!),
            onChangeRangeDistribution: isDisabled ? undefined : handleChangeDistribution(ad._id!),
            screens: filteredScreens,
            status: ad.visible ? 'default' : 'notVisible',
            subtitle: formatMessage({
              defaultMessage: ad.adFormat,
              id: `multiFlightCommon.bannerFormats.${ad.adWidth}x${ad.adHeight}${ad.adFormat}`
            }),
            title: ad.campaignName
          };
        });

  const defaultSelectedItems = compositionData.filter(i => selectedAdIds.includes(i.id));
  const selectedAds = entity.ads.filter(i => selectedAdIds.includes(i._id!));

  const clearSelection = () => {
    setState(prevState => ({
      ...prevState,
      selectedAdIds: []
    }));
  };

  const handleDeleteSelectedAds = () => {
    handleAdsChange(currentAds => currentAds.filter(ad => !selectedAdIds.includes(ad._id!)));
    clearSelection();
  };

  const handleEditSelectedAds = () => {
    setState(prevState => ({
      ...prevState,
      editedAds: selectedAds.map(ad => cloneDeep(ad))
    }));
    clearSelection();
  };

  const handleHideSelectedAds = () => {
    handleAdsChange(currentAds => {
      return currentAds.map(ad => {
        if (selectedAdIds.includes(ad._id!)) {
          return {
            ...ad,
            visible: !ad.visible
          };
        }
        return ad;
      });
    });
    clearSelection();
  };

  const handleDistributionColumnChange = (_id: string, checked: boolean) => {
    onAdsDistributionToggled(checked);
    setState(prevState => ({
      ...prevState,
      showDistributionColumn: checked
    }));
    handleAdsChange(currentAds => {
      if (!checked) {
        return currentAds.map(ad => ({
          ...ad,
          distribution: 0
        }));
      } else {
        return equalizeAdsDistribution(currentAds);
      }
    });
  };

  const handleAdPriceColumnChange = (_id: string, checked: boolean) => {
    onAdsPriceToggled(checked);
    setState(prevState => ({
      ...prevState,
      showAdPriceColumn: checked
    }));
    handleAdsChange(currentAds => {
      if (!checked) {
        return currentAds.map(ad => ({
          ...ad,
          bidPrice: 0
        }));
      }
      return currentAds;
    });
  };

  const handleCustomEventsChange = (_id: string, checked: boolean) => {
    onCustomEventsToggled(checked);
  };

  const devicePlatforms = (entity.targeting?.devicePlatform
    ?.filter(dp => dp.enable)
    .map(dp => dp.name) as DevicePlatformEnum[]) ?? [DevicePlatformEnum.mobile];

  return (
    <>
      <div className="adCreatives">
        <div className="adCreatives__box" id={'creatives_adCreatives_box'}>
          <header className="adCreatives__boxTitle">
            {formatMessage({
              defaultMessage: 'Ad Creatives',
              id: 'creatives.adCreatives.title'
            })}
          </header>
          <div>
            {!!errors?.ads?.message && showErrorToast && (
              <Toast
                autoClose={false}
                className="adCreatives__errorToast"
                closeToast={() =>
                  setState(prevState => ({
                    ...prevState,
                    showErrorToast: false
                  }))
                }
                icon="fal fa-exclamation-triangle"
                text={errors?.ads?.message}
                type="danger"
              />
            )}
            <Switch>
              <Switch.Case condition={isFetchingPublishersDictionary || isFetchingCreatives}>
                <Loader id="adCreatives_spinner" type="landing" variant="default">
                  <Loader.Title>
                    {formatMessage({
                      defaultMessage: 'Ad Creatives is loading! This process will take a few seconds...',
                      id: 'creatives.adCreatives.spinner.title'
                    })}
                  </Loader.Title>
                </Loader>
              </Switch.Case>
              <Switch.Case condition={Boolean(entity.ads[0])}>
                <div className="adCreatives__switchContainer">
                  {entityType === 'lineItem' && showDistribution && (
                    <Checkbox
                      active={showDistributionColumn}
                      classes="adCreatives__switchSGO"
                      defaultActive={isDistributionColumnActive}
                      id="adCreatives-checkxbox-sgo"
                      labelText={formatMessage({
                        defaultMessage: 'Set Goal Distribution',
                        id: 'creatives.adCreatives.switch.sgo'
                      })}
                      onClickAction={(_id: string, checked: boolean) => handleDistributionColumnChange(_id, checked)}
                      type="switch"
                    />
                  )}
                  {showAdPrice && (
                    <Checkbox
                      active={showAdPriceColumn}
                      classes="adCreatives__switchAdPrice"
                      defaultActive={isAdPriceColumnActive}
                      id="adCreatives-checkxbox-adPrice"
                      labelText={formatMessage({
                        defaultMessage: 'Set Ad Price',
                        id: 'creatives.adCreatives.switch.adPrice'
                      })}
                      onClickAction={(_id: string, checked: boolean) => handleAdPriceColumnChange(_id, checked)}
                      type="switch"
                    />
                  )}
                  {showCustomEvents && (
                    <Checkbox
                      active={isCustomEventsActive}
                      classes="adCreatives__switchCustomEvents"
                      defaultActive={isCustomEventsActive}
                      id="adCreatives-checkxbox-customEvents"
                      labelText={formatMessage({
                        defaultMessage: 'Custom Events',
                        id: 'creatives.adCreatives.switch.customEvents'
                      })}
                      onClickAction={(_id: string, checked: boolean) => handleCustomEventsChange(_id, checked)}
                      type="switch"
                    />
                  )}
                </div>
                <div className="adCreatives__content">
                  <div className="adCreatives__contentOptions">
                    <InputSearch
                      id="doohInventoryFilters-searchByName"
                      lens
                      name="publisherName"
                      onChange={handleSearchChange}
                      placeholder={formatMessage({
                        defaultMessage: 'Search by name...',
                        id: 'creatives.adCreatives.table.filter.search'
                      })}
                    />
                    <span className="adCreatives__actionsContainer">
                      <MacrosList countries={entity.countries} />
                      <BulkActions
                        ads={selectedAds}
                        onDeleteSelected={handleDeleteSelectedAds}
                        onEditSelected={handleEditSelectedAds}
                        onHideSelected={handleHideSelectedAds}
                      />
                      <AddCreatives
                        aspectRatiosByAdType={aspectRatiosByAdType}
                        countries={entity.countries}
                        devicePlatforms={devicePlatforms}
                        entityFamily={entity.type as CampaignFamilyEnum}
                        entityId={entity._id as string}
                        entityType={entityType}
                        onSave={handleAddAdCreatives}
                        onVenuesChange={onVenuesChange}
                        screens={screens}
                        user={user}
                        userRoles={userRoles}
                        venues={venues}
                      />
                    </span>
                  </div>
                  <div
                    className="adCreatives__contentTable"
                    style={
                      {
                        '--adCreatives-columns': gridLayout.columns,
                        '--adCreatives-headerAreas': gridLayout.headerAreas,
                        '--adCreatives-rowAreas': gridLayout.rowAreas
                      } as React.CSSProperties
                    }
                  >
                    <TaptapList
                      className="adCreatives__table"
                      data={compositionData}
                      defaultSelectedItems={defaultSelectedItems}
                      id="adCreatives_table"
                      key={`list-${showAdPriceColumn}-${showDistributionColumn}`}
                      onSelectionChange={handleSelectedAdIdsChange}
                      selectionType={compositionData.length > 1 ? 'rowClick' : undefined}
                    >
                      <AdCreativesColumns
                        id="adCreatives__tableColumns"
                        toggleAutoDistribution={toggleAutoDistribution}
                        type={entity.type}
                        visibleColumns={gridLayout.visible}
                      />
                      <TaptapList.RowsList<TaptapListItem>
                        emptyStateDescription={formatMessage({
                          defaultMessage: 'Try adjusting your search or filters to find creatives.',
                          id: 'creatives.adCreatives.emptyState.description'
                        })}
                        emptyStateIcon="fal fa-empty-set"
                        emptyStateTitle={formatMessage({
                          defaultMessage: 'No results match your filters',
                          id: 'creatives.adCreatives.emptyState.title'
                        })}
                      >
                        {itemsList =>
                          itemsList.map(item => (
                            <TaptapList.RowsList.Row id={item.id} key={item.id}>
                              <div className="adCreatives__tableRowsListRow__title">
                                <div className="adCreatives__tableRowsListRow__title__text">
                                  <FieldEdition
                                    id={`adCreative-${item.id}`}
                                    onChange={(value: string) => handleNameChange(item.id, value)}
                                    title={item.title}
                                    value={item.title}
                                  />
                                </div>
                              </div>
                              <div className="adCreatives__tableRowsListRow__subtitle">
                                <span className="adCreatives__tableRowsListRow__format">{item.format}</span>
                                {item.ad.adFormat !== 'daast' && (
                                  <span className="adCreatives__tableRowsListRow__resolution">
                                    ({item.ad.adWidth}x{item.ad.adHeight})
                                    <AspectRatioResolutionsTooltip
                                      aspectRatio={item.aspectRatio}
                                      maxResolution={`${item.ad.adWidth}x${item.ad.adHeight}`}
                                    />
                                  </span>
                                )}
                              </div>
                              <div className="adCreatives__tableRowsListRow__duration">
                                <Duration ad={item.ad} creative={item.creative} />
                              </div>
                              {gridLayout.visible.behavior && (
                                <div className="adCreatives__tableRowsListRow__behavior">
                                  <Behavior ad={item.ad} creative={item.creative} />
                                </div>
                              )}
                              {gridLayout.visible.qr && (
                                <div className="adCreatives__tableRowsListRow__qr">
                                  <QREnabled ad={item.ad} creative={item.creative} />
                                </div>
                              )}
                              {gridLayout.visible.adTypeScreens && (
                                <div className="adCreatives__tableRowsListRow__adTypeScreens">
                                  <AdTypeScreens
                                    ad={item.ad}
                                    creative={item.creative}
                                    currencyInfo={currencyInfo}
                                    entityFamily={entity.type}
                                    screens={item.screens}
                                  />
                                </div>
                              )}
                              <div className="adCreatives__tableRowsListRow__group">
                                <Group
                                  defaultValue={entity.type === 'dooh' ? item.ad.screenGroups : item.ad.zoneGroups}
                                  groups={creativeGroups}
                                  onChange={(groups: string[]) => handleGroupsChange(item.id, groups)}
                                />
                              </div>
                              {gridLayout.visible.distribution && (
                                <div className="adCreatives__tableRowsListRow__distribution">
                                  <Distribution
                                    ad={item.ad}
                                    baseCurrency={currencyInfo}
                                    budgetValue={item.budgetValue}
                                    error={item.error}
                                    onChangeBudgetDistribution={item.onChangeBudgetDistribution}
                                    onChangeInputDistribution={item.onChangeInputDistribution}
                                    onChangeRangeDistribution={item.onChangeRangeDistribution}
                                  />
                                </div>
                              )}
                              {gridLayout.visible.adPrice && (
                                <div className="adCreatives__tableRowsListRow__adPrice">
                                  <AdPrice
                                    ad={item.ad}
                                    baseCurrency={currencyInfo}
                                    bidPrice={item.ad.bidPrice}
                                    id={item.id}
                                    onChange={handleAdPriceChange(item.id)}
                                  />
                                </div>
                              )}
                              <div className="adCreatives__tableRowsListRow__adStatus">
                                <AdStatus
                                  ads={[item.ad]}
                                  campaignId={entity._id}
                                  campaignName={entity.name}
                                  campaignType={entity.type}
                                  publishersDictionary={publishersDictionary}
                                  updateAdsAdExchangesValidationCallback={(
                                    exchangeCode: string,
                                    adForValidate: AdInterface<string>
                                  ) => handleUpdateAdsValidationCallback(exchangeCode, adForValidate, entity)}
                                  user={user}
                                  userRoles={userRoles}
                                />
                              </div>
                              {gridLayout.visible.preview && Boolean(item.ad._id) && (
                                <Tooltip
                                  config={{
                                    arrow: true,
                                    interactions: [TooltipInteractionEnum.hover],
                                    placement: 'bottom',
                                    portal: true,
                                    strategy: 'absolute',
                                    text: item.isDirty
                                      ? formatMessage({
                                          defaultMessage: "Save your changes to see the creative's preview",
                                          id: 'creatives.adCreatives.table.preview.tooltip.disabled'
                                        })
                                      : formatMessage({
                                          defaultMessage: "See creative's preview",
                                          id: 'creatives.adCreatives.table.preview.tooltip'
                                        })
                                  }}
                                >
                                  {/* The tooltip must hover-trigger off this wrapper, not the button itself:
                                      ButtonUI's disabled "link" shape sets pointer-events: none, which would
                                      otherwise swallow the hover and hide the "save first" tooltip. */}
                                  <div className="adCreatives__tableRowsListRow__preview">
                                    <ButtonUI
                                      disabled={item.isDirty}
                                      icon="fa-kit fa-rectangular-image"
                                      id={`preview-button-creative${item.id}`}
                                      onClick={() =>
                                        window.open(
                                          buildPreviewUrl(item.ad, entity, entityType, previewPosition),
                                          '_blank',
                                          'noopener,noreferrer'
                                        )
                                      }
                                      shape="link"
                                      size="ultraSmall"
                                      type="secondary"
                                    />
                                  </div>
                                </Tooltip>
                              )}
                              <div className="adCreatives__tableRowsListRow__switch">
                                <Checkbox
                                  active={item.ad.visible}
                                  classes="adCreatives__activationSwitch"
                                  disabled={false}
                                  id={`creative-${item.id}`}
                                  onClickAction={(
                                    _: string,
                                    visible: boolean,
                                    ev: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    ev.stopPropagation();
                                    handleVisibilityChange(item.id, visible);
                                  }}
                                  tiny
                                  type="switch"
                                />
                              </div>
                              <div className="adCreatives__tableRowsListRow__actions">
                                <ContextualMenu
                                  buttons={[
                                    {
                                      icon: 'fal fa-pen-to-square',
                                      id: `row-edit-button-creative${item.id}`,
                                      onClick: () => handleEditCreative(item.ad),
                                      text: formatMessage({
                                        defaultMessage: 'Edit',
                                        id: 'common.edit'
                                      })
                                    },
                                    {
                                      icon: 'fal fa-trash',
                                      id: `row-delete-button-creative${item.id}`,
                                      onClick: () => handleDeleteCreative(item.id),
                                      text: formatMessage({
                                        defaultMessage: 'Delete',
                                        id: 'common.delete'
                                      })
                                    }
                                  ]}
                                  direction="vertical"
                                  id={item.id}
                                />
                              </div>
                            </TaptapList.RowsList.Row>
                          ))
                        }
                      </TaptapList.RowsList>
                    </TaptapList>
                  </div>
                </div>
              </Switch.Case>
              <Switch.Default>
                <EmptyState
                  description={formatMessage({
                    defaultMessage:
                      "Let's start adding your ads creatives live -- upload or create a new one to begin.",
                    id: 'creatives.adCreatives.emptyState.subtitle'
                  })}
                  fontSizes={{
                    description: '12px',
                    icon: '21px',
                    title: '14px'
                  }}
                  id="creatives_adCreatives_emptyState"
                  show
                  spacing="small"
                  title={formatMessage({
                    defaultMessage: 'Start Adding your Ads Creatives',
                    id: 'creatives.adCreatives.emptyState.title'
                  })}
                >
                  <AddCreatives
                    aspectRatiosByAdType={aspectRatiosByAdType}
                    className="adCreatives__emptyStateButton"
                    countries={entity.countries}
                    devicePlatforms={devicePlatforms}
                    entityFamily={entity.type as CampaignFamilyEnum}
                    entityId={entity._id as string}
                    entityType={entityType}
                    onSave={handleAddAdCreatives}
                    onVenuesChange={onVenuesChange}
                    screens={screens}
                    user={user}
                    userRoles={userRoles}
                    venues={venues}
                  />
                </EmptyState>
              </Switch.Default>
            </Switch>
          </div>
        </div>
      </div>
      {editedAds.length !== 0 && (
        <CreativesModal
          ads={editedAds}
          aspectRatiosByAdType={aspectRatiosByAdType}
          countries={entity.countries}
          creatives={creatives?.filter(({ _id }) => editedAds.some(ad => ad.creative === _id)) || []}
          currencyInfo={currencyInfo}
          devicePlatforms={devicePlatforms}
          entityFamily={entity.type}
          entityId={entity._id as string}
          entityType={entityType}
          isModalOpened={editedAds.length !== 0}
          mode="edit"
          onModalClose={handleCloseEditCreativeModal}
          onSave={handleConfirmEditCreative}
          onVenuesChange={onVenuesChange}
          prefixId="edit-creative"
          user={{
            ...user,
            _id: user.id || user._id
          }}
          userRoles={userRoles}
          venues={venues}
        />
      )}
    </>
  );
}
